/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import FormControl from 'react-bootstrap/FormControl';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { Tooltip, Badge, Popper, ClickAwayListener } from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import Filters from 'features/common/Filters';
import debounce from 'utils/debounce';
import T from 'i18n';
import { useDispatch } from 'react-redux';
import { clearSearchedFiles, fetchSearchedFiles } from 'features/case/redux/fetchSearchedFiles';
import { selectClickEnterToPresent, selectCurrentCaseId } from 'common/selectors';
import { useSelector } from 'react-redux';
import { safeGetJSON, atomicStorageOperations } from './hooks/usePresentView';
import './index.scss';

interface SearchConfig {
  documents?: any[];
  isAllDocsWithLongList?: boolean;
  isPresentModeWithBackEnd?: boolean;
  isPresentModePage?: boolean;
  isDocFinderPage?: boolean;
  isSearchModePage?: boolean;
  isReady?: boolean;
  placeholder?: string;
  selectedColumns?: string[];
  isClickEnterToPresent?: boolean;
  isAnnotation?: boolean;
  isLoading?: boolean;
  hideFilterOptions?: boolean;
  showGoToPageFilter?: boolean;
  useOnlyIdColForPresentMode?: boolean;
  showDeepSearch?: boolean;
}

interface FiltersState {
  goToGlobalPageIdx: string;
  authors: any[];
  recipients: any[];
  createdBy: any[];
  tags: any[];
  dateFrom: Date | null;
  dateTo: Date | null;
  dateFilter: boolean;
  withAnnotations: boolean;
  withoutAnnotations: boolean;
  public: boolean;
  private: boolean;
  withPublicHyperlinks: boolean;
  withoutPublicHyperlinks: boolean;
  trialBundles: any[];
  shared: boolean;
  notShared: boolean;
  withFlag: boolean;
  noFlag: boolean;
  withMerged: boolean;
  notMerged: boolean;
}

interface SearchAllFilesProps {
  search?: SearchConfig;
  style?: React.CSSProperties;
  presentView?: boolean;
  hasSearch?: boolean;
  clickToSearch?: boolean;
  hasDocFinderFilter?: boolean;
  setShowDocFinderFilter?: (val: boolean) => void;
  showDocFinderFilter?: boolean;
  isBundleTabPage?: boolean;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  setSearchError: React.Dispatch<React.SetStateAction<string>>;
  setDocsToDisplay: React.Dispatch<React.SetStateAction<any[]>>;
  handleOpenDoc: any;
  docsToDisplay: any[];
  startStopPresentHandlerWPresentView: any;
  isSearching: boolean;
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
  viewerControlRef: React.RefObject<any>;
  recentSearches?: Record<string, any>;
  requestIdRef?: React.MutableRefObject<number>; // Add requestIdRef prop
}

function SearchAllFiles({
  search,
  style,
  presentView,
  hasSearch = true,
  clickToSearch,
  hasDocFinderFilter,
  setShowDocFinderFilter,
  showDocFinderFilter,
  isBundleTabPage,
  value,
  setValue,
  setSearchError,
  setDocsToDisplay,
  handleOpenDoc,
  docsToDisplay,
  isSearching,
  setIsSearching,
  viewerControlRef,
  recentSearches,
  requestIdRef,
}: SearchAllFilesProps) {
  /**
   * State
   */
  const [filters, setFilters] = useState<FiltersState>({
    goToGlobalPageIdx: '',
    authors: [],
    recipients: [],
    createdBy: [],
    tags: [],
    dateFrom: null,
    dateTo: null,
    dateFilter: false,
    withAnnotations: false,
    withoutAnnotations: false,
    public: false,
    private: false,
    withPublicHyperlinks: false,
    withoutPublicHyperlinks: false,
    trialBundles: [],
    shared: false,
    notShared: false,
    withFlag: false,
    noFlag: false,
    withMerged: false,
    notMerged: false,
  });
  const [filtersCount, setFiltersCount] = useState(0);
  const [freshSearch, setFreshSearch] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filterPosition, setFilterPosition] = useState<HTMLElement | null>(null);
  // Use requestIdRef from props, or create local one if not provided (for backwards compatibility)
  const localRequestIdRef = useRef<number>(0);
  const currentRequestIdRef = requestIdRef || localRequestIdRef;

  const isReady = search?.isReady;

  const dispatch = useDispatch();
  const isClickEnterToPresent = useSelector(selectClickEnterToPresent);
  const caseId = useSelector(selectCurrentCaseId);

  // Unified cache check function to avoid duplication
  const attachSearchContext = useCallback((doc: any, context: string) => {
    if (!doc || typeof doc !== 'object') return doc;
    // Preserve existing context if already present
    if ((doc as any).searchContext === context) return doc;
    return { ...doc, searchContext: context };
  }, []);

  const checkCache = useCallback(
    (term: string, currentRequestId: number) => {
      if (term.length <= 2 || !recentSearches || !caseId) return null;

      const currentStoredData = safeGetJSON(localStorage, `${caseId}.recentSearches`, {});
      if (Object.keys(currentStoredData).length === 0) return null;

      const match = Object.values(currentStoredData).find(
        (entry: any) => String(entry?.displayPage?.toUpperCase()) === term?.toUpperCase(),
      );

      if (!match) return null;

      // Only update if this is still the latest request
      if (currentRequestId === currentRequestIdRef.current) {
        const matchWithContext = attachSearchContext(match, term);
        // Show the recently-presented match instantly as a first paint, but do NOT
        // stop the search here (and keep `isSearching` true): the backend search still
        // runs and its complete results take over. This is required so duplicate
        // external IDs (e.g. two docs sharing "RW/187") both surface — the cache only
        // ever holds the single doc that was actually presented.
        setDocsToDisplay([matchWithContext]);
        return matchWithContext;
      }
      return null;
    },
    [recentSearches, caseId, setDocsToDisplay, setIsSearching, attachSearchContext],
  );

  const sfHandler = {
    searchChanged: (val: string) => {},
    deepSearchChanged: (term: any) => {
      // Increment request ID to track this specific search
      const currentRequestId = ++currentRequestIdRef.current;

      setIsSearching(true);

      // Paint any recently-presented match instantly, then continue to the
      // authoritative backend search so duplicate external IDs both show.
      checkCache(term, currentRequestId);

      dispatch(
        fetchSearchedFiles({
          searchTerm: term,
          isDocFinderPage: true,
          isSearchModePage: false,
          isPresentModeWithBackEnd: false,
          requestId: currentRequestId,
        }),
      )
        .then((res: any) => {
          // Only update if this is still the latest request
          if (currentRequestId === currentRequestIdRef.current) {
            const results = res.results || [];
            const resultsWithContext = Array.isArray(results)
              ? results.map((item: any) => attachSearchContext(item, term))
              : [];
            setDocsToDisplay(resultsWithContext);
            setIsSearching(false);

            // Persist search results and search value to survive component resets from handleOpenDoc
            if (results.length > 0 && term.trim() && caseId) {
              // Use persistDocsToDisplayWithContext which includes the search value
              atomicStorageOperations
                .persistDocsToDisplayWithContext(
                  caseId,
                  resultsWithContext,
                  term,
                  currentRequestId,
                )
                .catch((error) => {
                  console.error('Failed to persist search state:', error);
                });
            } else if (term.trim() && caseId) {
              // Even if no results, persist the search value
              atomicStorageOperations.persistSearchValue(caseId, term).catch((error) => {
                console.error('Failed to persist search value:', error);
              });
            }
          }
        })
        .catch((err: any) => {
          if (currentRequestId === currentRequestIdRef.current) {
            console.error('Error fetching files:', err);
            setIsSearching(false);
          }
        });
    },

    filterChanged: (filters: any) => {},

    search: (val: any) => {},

    searchNext: () => {},

    clearSearchedFiles: (allDocs: any) => {
      dispatch(clearSearchedFiles());
    },

    presentToHearingRoom: (allDocs: any, val: any) => {},
    presentFirstDoc: (allDocs: any, val: any) => {},
  };

  /**
   * Debounce references
   *
   * We use useRef + function assignment so we don't recreate
   * the debounced functions on each render, preventing re-subscription.
   */
  const debounceFnRef = useRef<((val: string) => void) | null>(null);
  const debounceDeepSearchFnRef = useRef<((val: string) => void) | null>(null);

  // Clear both UI state and persisted search context to avoid stale re-hydration
  const clearSearchStateSafelyInput = useCallback(async () => {
    setValue('');
    setDocsToDisplay([]);
    setIsSearching(false);
    try {
      await atomicStorageOperations.clearSearchState(caseId);
      await atomicStorageOperations.persistDocsToDisplayWithContext(
        caseId,
        [],
        '',
        currentRequestIdRef.current,
      );
    } catch (err) {
      console.error('Failed to clear search state from input clear:', err);
    }
  }, [caseId, setValue, setDocsToDisplay, setIsSearching]);

  // Create the deep search debounced function just once
  if (!debounceDeepSearchFnRef.current) {
    debounceDeepSearchFnRef.current = debounce((val: string) => {
      if (!val) {
        setIsSearching(false);
        return;
      }
      if (!search) return;

      const { isPresentModeWithBackEnd, isAllDocsWithLongList } = search;
      const length = val.length;

      if (
        (length > 1 && isPresentModeWithBackEnd) ||
        (length > 0 && !isPresentModeWithBackEnd && !isAllDocsWithLongList) ||
        (length > 2 && isAllDocsWithLongList)
      ) {
        // Increment request ID for this search to track latest request
        const currentRequestId = ++currentRequestIdRef.current;

        setIsSearching(true);

        // Paint any recently-presented match instantly, then continue to the
        // authoritative backend search so duplicate external IDs both show.
        checkCache(val, currentRequestId);

        dispatch(
          fetchSearchedFiles({
            searchTerm: val,
            isDocFinderPage: true,
            isSearchModePage: false,
            isPresentModeWithBackEnd: false,
            requestId: currentRequestId, // Add request ID to track this specific request
          }),
        )
          .then((res: any) => {
            // Only update if this is still the latest request
          if (currentRequestId === currentRequestIdRef.current) {
            const results = res.results || [];
            const resultsWithContext = Array.isArray(results)
              ? results.map((item: any) => attachSearchContext(item, val))
              : [];
            setDocsToDisplay(resultsWithContext);
            setIsSearching(false);

            // Persist search results and search value to survive component resets from handleOpenDoc
            if (results.length > 0 && val.trim() && caseId) {
              // Use persistDocsToDisplayWithContext which includes the search value
              atomicStorageOperations
                .persistDocsToDisplayWithContext(
                  caseId,
                  resultsWithContext,
                  val,
                  currentRequestId,
                )
                .catch((error) => {
                  console.error('Failed to persist search state:', error);
                });
            } else if (val.trim() && caseId) {
              // Even if no results, persist the search value
                atomicStorageOperations.persistSearchValue(caseId, val).catch((error) => {
                  console.error('Failed to persist search value:', error);
                });
              }
            }
          })
          .catch((err: any) => {
            if (currentRequestId === currentRequestIdRef.current) {
              console.error('Error fetching files:', err);
              setIsSearching(false);
            }
          });
      }
    }, 500);
  }

  const clearDocumentSelection = useCallback(() => {
    if (search?.documents) {
      search.documents.forEach((doc) => {
        if (doc.selected) {
          doc.selected = false;
        }
      });
    }
  }, [search]);

  const searchHandler = useCallback(() => {
    try {
      if (freshSearch) {
        const doSearch = debounce(() => {
          sfHandler.search?.(value);
          setFreshSearch(false);
        }, 250);
        doSearch();
      } else {
        sfHandler.searchNext?.();
      }
    } catch (error) {
      console.error(error);
      setSearchError(T.translate('case.presentView.errors.searchError'));
    }
  }, [freshSearch, value]);
  const onChangeHandler = useCallback(
    (newVal: string) => {
      setValue(newVal);
      setFreshSearch(true);

      const trimmedValue = newVal.replace(/\s*\/\s*/g, '/');

      if (!debounceFnRef.current) {
        debounceFnRef.current = debounce((val: string) => {
          clearDocumentSelection();
          sfHandler?.searchChanged?.(val);
        }, 350);
      }
      debounceFnRef.current(trimmedValue);
    },
    [clearDocumentSelection, sfHandler, setValue],
  );

  const onChangeHandlerForLongList = useCallback(
    (newVal: string) => {
      setValue((prevVal) => {
        if (newVal.length < 3 && prevVal.length > newVal.length) {
          // If user is deleting chars, might need to trigger empty
          if (!debounceFnRef.current) {
            debounceFnRef.current = debounce((val: string) => {
              clearDocumentSelection();
              sfHandler?.searchChanged?.(val);
            }, 500);
          }
          debounceFnRef.current('');
        }
        return newVal;
      });
      setFreshSearch(true);

      if (!search) return;
      const { isPresentModePage, isAllDocsWithLongList } = search;
      const length = newVal.length;

      const meetsDeepSearch =
        // e.g. partial IDs or references
        (length === 2 && /[a-zA-Z]/.test(newVal[0]) && /[0-9]/.test(newVal[1])) ||
        (length === 3 &&
          /[a-zA-Z]/.test(newVal[0]) &&
          /[0-9]/.test(newVal[1]) &&
          /[0-9]/.test(newVal[2])) ||
        (length > 3 && isPresentModePage) ||
        (length > 2 && isAllDocsWithLongList);

      if (meetsDeepSearch) {
        const trimmedValue = newVal.replace(/\s*\/\s*/g, '/');
        if (!debounceFnRef.current) {
          debounceFnRef.current = debounce((val: string) => {
            clearDocumentSelection();
            sfHandler?.searchChanged?.(val);
          }, 500);
        }
        debounceFnRef.current(trimmedValue);
      }
    },
    [clearDocumentSelection, search, setValue],
  );

  const filterHandler = useCallback(
    (val: FiltersState) => {
      // Adjust filtersCount
      for (const key of Object.keys(val)) {
        setFiltersCount((prevCount) => {
          const oldVal = (filters as any)[key];
          const newVal = (val as any)[key];

          // If array => difference in length
          if (Array.isArray(oldVal) || Array.isArray(newVal)) {
            const oldLen = Array.isArray(oldVal) ? oldVal.length : 0;
            const newLen = Array.isArray(newVal) ? newVal.length : 0;
            return prevCount + (newLen - oldLen);
          } else if (oldVal !== newVal && newVal) {
            // skip certain fields
            if (['goToGlobalPageIdx', 'dateFrom', 'dateTo'].includes(key)) {
              return prevCount;
            }
            return prevCount + 1;
          } else if (oldVal !== newVal && !newVal && prevCount > 0) {
            if (['goToGlobalPageIdx', 'dateFrom', 'dateTo'].includes(key)) {
              return prevCount;
            }
            return prevCount - 1;
          }
          return prevCount;
        });
      }

      setFilters(val);
      clearDocumentSelection();
      sfHandler?.filterChanged?.(val);
    },
    [clearDocumentSelection, filters],
  );

  const handleFilterClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // If external doc finder filter usage
      if (hasDocFinderFilter && setShowDocFinderFilter) {
        setShowDocFinderFilter(!showDocFinderFilter);
      } else {
        setShowFilters(true);
        setFilterPosition(event.currentTarget);
      }
    },
    [hasDocFinderFilter, setShowDocFinderFilter, showDocFinderFilter],
  );

  const handleClickAway = useCallback(() => {
    setShowFilters(false);
  }, []);

  const formStyle: React.CSSProperties = {
    ...(hasSearch ? {} : { display: 'none' }),
    ...(presentView ? { display: 'flex', flex: 1 } : {}),
    ...style,
  };

  return (
    <>
      <div style={{ width: '100%' }}>
        <Form onSubmit={(e: any) => e.preventDefault()} inline={!presentView} style={formStyle}>
          {search?.showGoToPageFilter && !isBundleTabPage && !(sfHandler && sfHandler.search) && (
            <div style={{ display: 'flex', marginRight: '1rem' }}>
              <FormControl
                type="search"
                placeholder={T.translate('case.goToGlobalPage')}
                style={{ marginRight: '0.5rem' }}
                value={filters.goToGlobalPageIdx}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // Only clear docs if we're actually changing the filter, not during restoration
                  if (filters.goToGlobalPageIdx !== e.target.value) {
                    setDocsToDisplay([]);
                  }
                  const numericValue = e.target.value.replace(/[^\d.]+/g, '');
                  const newFilters = { ...filters, goToGlobalPageIdx: numericValue };
                  filterHandler(newFilters);
                  setFilters(newFilters);
                }}
                onFocus={() => {
                  // Only clear if value is empty AND we have no persisted search results
                  if (value === '' && docsToDisplay.length === 0) setDocsToDisplay([]);
                  // if (search?.documents && sfHandler?.clearSearchedFiles) {
                  //   sfHandler.clearSearchedFiles(search.documents);
                  // }
                }}
                onBlur={() => {}}
                disabled={!isReady}
              />
            </div>
          )}

          {search && Object.keys(search).length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                cursor: clickToSearch ? 'pointer' : 'auto',
              }}
            >
              <FormControl
                type="search"
                placeholder={search.placeholder || T.translate('case.search')}
                style={{ flex: 1, marginRight: '0.5rem' }}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const trimmed = e.target.value.trimStart();

                  // Only clear docs if we're starting a new search, not during restoration
                  if (trimmed !== value) {
                    setDocsToDisplay([]);
                  }

                  // if isPresentModeWithBackEnd, clearing files first
                  if (search.isPresentModeWithBackEnd) {
                    dispatch(clearSearchedFiles());
                  }

                  if (
                    (search.isPresentModePage && search.useOnlyIdColForPresentMode) ||
                    search.isAllDocsWithLongList
                  ) {
                    onChangeHandlerForLongList(trimmed);
                  } else {
                    onChangeHandler(trimmed);
                  }

                  if (
                    search.showDeepSearch ||
                    search.isSearchModePage ||
                    search.isDocFinderPage ||
                    search.isPresentModeWithBackEnd
                  ) {
                    debounceDeepSearchFnRef.current?.(trimmed);
                  }
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  // When the native clear button is used, value becomes '' but React's onChange may not fire.
                  if (e.currentTarget.value === '' && value !== '') {
                    clearSearchStateSafelyInput();
                  }
                }}
                onFocus={() => {
                  // Only clear if value is empty AND we have no persisted search results
                  if (value === '' && docsToDisplay.length === 0) setDocsToDisplay([]);
                }}
                onBlur={() => {}}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter' && isClickEnterToPresent) {
                    if (docsToDisplay.length === 1 && docsToDisplay[0].private !== true) {
                      // open the document and immediately present it
                      handleOpenDoc(docsToDisplay[0], viewerControlRef);
                    }

                    searchHandler();
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
                disabled={!isReady || clickToSearch}
                readOnly={!!clickToSearch}
                autoFocus={true}
                className={isSearching && value.length > 0 ? 'pulse-blue' : ''}
              />
            </div>
          )}

          {!(sfHandler && sfHandler.search) && !(search && search.hideFilterOptions) && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={T.translate('case.filter')}>
                  <div
                    onClick={handleFilterClick}
                    style={{
                      cursor: !isReady ? 'not-allowed' : 'pointer',
                      marginRight: 8,
                    }}
                  >
                    <Badge badgeContent={filtersCount} color="primary">
                      <FilterListRoundedIcon />
                    </Badge>
                  </div>
                </Tooltip>
                <Popper
                  open={Boolean(showFilters)}
                  anchorEl={filterPosition}
                  placement="bottom-end"
                  disablePortal
                >
                  <Filters
                    defaultFilters={filters}
                    filterHandler={filterHandler}
                    documents={search?.documents}
                    columns={search?.selectedColumns}
                    isAnnotation={!!search?.isAnnotation}
                  />
                </Popper>
              </div>
            </ClickAwayListener>
          )}

          {!presentView && (
            <Button
              variant="light"
              onClick={searchHandler}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                spin={!!search?.isLoading}
                style={{ marginRight: 4 }}
              />
            </Button>
          )}
        </Form>
      </div>
    </>
  );
}

export default SearchAllFiles;
